import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { z } from 'zod'
import {
  ArrowRight,
  AtSign,
  Inbox,
  Loader2,
  MessageCircle,
} from 'lucide-react'
import { api } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'

/** API response shape for `/me/replies`. */
const replyRow = z.object({
  id: z.string(),
  taskId: z.string(),
  taskName: z.string(),
  spaceId: z.string().nullable(),
  spaceName: z.string().nullable(),
  listId: z.string().nullable(),
  listName: z.string().nullable(),
  authorId: z.string(),
  authorFullName: z.string(),
  authorAvatarUrl: z.string().nullable(),
  body: z.string(),
  mentionsMe: z.boolean(),
  createdAt: z.string(),
})
const repliesResponse = z.object({
  data: z.array(replyRow),
  nextCursor: z
    .object({
      cursorCreatedAt: z.string(),
      cursorId: z.string(),
    })
    .nullable(),
})

/** Relative time formatter — same cadence the Inbox uses ("3 minutes ago"
 *  → "2 hours ago" → "5 days ago" → DD/MM/YYYY fallback). Keeps the
 *  Replies feed consistent with the Messages page so a user moving
 *  between the two reads the same time grammar. */
function fmtRelative(iso) {
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  return formatDateTime(iso)
}

/** Initials fallback when the author has no avatar — matches the Inbox
 *  pattern so author chips look identical across pages. */
function initials(name) {
  const parts = String(name ?? '').trim().split(/\s+/)
  if (parts.length === 0 || parts[0] === '') return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Strip @[uuid] mention tokens out of the comment body so the preview
 *  reads naturally. The original mention markers stay in the database
 *  for rendering on the task detail view — this is purely a list-row
 *  cosmetic. */
function previewBody(raw) {
  return String(raw ?? '')
    .replace(/@\[[0-9a-f-]{36}\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Build the link back to the parent task. The Space page handles the
 *  list-and-task routing; clicking through opens that list and the
 *  user can scroll to the comment thread from there. Falls back to
 *  the all-tasks home if either id is missing (defensive — backend
 *  always sends both for accessible tasks). */
function linkForReply(r) {
  if (r.spaceId && r.listId) {
    return `/app/spaces/${r.spaceId}/lists/${r.listId}`
  }
  return '/app/focus-board'
}

/** One row in the reply feed. Designed to read like an email-style
 *  list item but with the comment body inline so the user gets the
 *  reply text without an extra click. */
function ReplyRow({ reply }) {
  return (
    <Link
      to={linkForReply(reply)}
      className="group block rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 hover:border-[color:var(--color-primary)]/40 hover:shadow-sm transition-all cursor-pointer"
    >
      <div className="flex items-start gap-3">
        {/* Author avatar — image when available, initials chip
            otherwise. Sized to match the InboxPage row chip so the
            visual rhythm carries between the two pages. */}
        {reply.authorAvatarUrl ? (
          <img
            src={reply.authorAvatarUrl}
            alt={reply.authorFullName}
            className="h-9 w-9 rounded-full object-cover shrink-0 mt-0.5"
          />
        ) : (
          <span className="h-9 w-9 rounded-full shrink-0 mt-0.5 flex items-center justify-center bg-[color:var(--color-accent-bg)] text-[color:var(--color-primary)] font-semibold text-xs">
            {initials(reply.authorFullName)}
          </span>
        )}

        <div className="flex-1 min-w-0">
          {/* Header line: who, where, when. Truncates aggressively so
              long task or space names don't push the timestamp off. */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[color:var(--color-ink-strong)] text-sm">
              {reply.authorFullName}
            </span>
            <span className="text-xs text-[color:var(--color-ink-muted)]">
              replied on
            </span>
            <span className="font-medium text-[color:var(--color-ink)] text-sm truncate max-w-[28ch]">
              {reply.taskName}
            </span>
            {reply.mentionsMe ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-[color:var(--color-accent-bg)] text-[color:var(--color-primary)]">
                <AtSign size={10} />
                mentioned you
              </span>
            ) : null}
            <span
              className="ml-auto text-xs text-[color:var(--color-ink-muted)] shrink-0"
              title={formatDateTime(reply.createdAt)}
            >
              {fmtRelative(reply.createdAt)}
            </span>
          </div>

          {/* Comment body preview — capped at 3 lines so a long
              comment doesn't dominate the feed. The full body is
              available on the task detail. */}
          <p className="mt-1.5 text-sm text-[color:var(--color-ink)] line-clamp-3 whitespace-pre-wrap">
            {previewBody(reply.body)}
          </p>

          {/* Footer crumb: which Space / List the task lives in.
              Helps the user disambiguate two tasks with the same
              name in different lists. */}
          {reply.spaceName || reply.listName ? (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
              {reply.spaceName ? <span>{reply.spaceName}</span> : null}
              {reply.spaceName && reply.listName ? (
                <ArrowRight size={10} aria-hidden="true" />
              ) : null}
              {reply.listName ? <span>{reply.listName}</span> : null}
              <span className="ml-auto text-[color:var(--color-primary)] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 font-medium">
                Open thread
                <ArrowRight size={12} />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  )
}

export function RepliesPage() {
  const repliesQuery = useInfiniteQuery({
    queryKey: ['me', 'replies'],
    queryFn: async ({ pageParam }) => {
      const params = { limit: 20 }
      if (pageParam) {
        params.cursorCreatedAt = pageParam.cursorCreatedAt
        params.cursorId = pageParam.cursorId
      }
      const res = await api.get('/me/replies', { params })
      return repliesResponse.parse(res.data)
    },
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    // Auto-refresh every 30s so new replies appear without a manual
    // reload. Matches the Messages page cadence (10s) but a touch
    // less frequent — replies are lower-volume than the full inbox.
    refetchInterval: 30_000,
  })

  // Flatten paginated rows into a single feed.
  const allReplies = useMemo(() => {
    const pages = repliesQuery.data?.pages ?? []
    return pages.flatMap((p) => p.data)
  }, [repliesQuery.data])

  // Pull the most-recent mention count out of the loaded feed so the
  // header subtitle can call out "X mention you" — a one-line reminder
  // that makes the page feel like a real inbox rather than just a
  // chronological log.
  const mentionsCount = useMemo(
    () => allReplies.filter((r) => r.mentionsMe).length,
    [allReplies],
  )

  return (
    <div>
      <Breadcrumb current="Replies" homePath="/app" homeLabel="Dashboard" />

      <div className="flex items-baseline justify-between gap-3 mt-2 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] leading-tight">
            Replies
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Replies on tasks you're on.
            {mentionsCount > 0 ? (
              <>
                {' '}
                <span className="font-medium text-[color:var(--color-primary)]">
                  {mentionsCount} mention {mentionsCount === 1 ? 's' : ''} you.
                </span>
              </>
            ) : null}
          </p>
        </div>
      </div>

      {/* Loading state — only when we don't yet have any cached page. */}
      {repliesQuery.isLoading ? (
        <div className="flex items-center justify-center py-16 text-[color:var(--color-ink-muted)] text-sm">
          <Loader2 size={18} className="animate-spin mr-2" />
          Loading replies…
        </div>
      ) : null}

      {/* Error state — shows the raw axios message so the user knows
          whether to retry or check connectivity. */}
      {repliesQuery.isError ? (
        <div className="rounded-xl border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 p-4 text-sm text-[color:var(--color-priority-urgent)]">
          We couldn't load your replies. Please refresh and try again.
        </div>
      ) : null}

      {/* Empty state — friendlier than the inbox's plain "Nothing here"
          because the page is brand-new and most users won't know what
          to expect from it. The icon + one-line explainer doubles as
          documentation. */}
      {!repliesQuery.isLoading && allReplies.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-primary)] mb-3">
            <Inbox size={22} />
          </div>
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] mb-1">
            No replies yet
          </h2>
          <p className="text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto">
            When someone responds to a task thread you started, were
            mentioned in, or are commenting on — their reply will
            land here.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
            <MessageCircle size={12} />
            Comment on a task to start a conversation.
          </div>
        </div>
      ) : null}

      {/* Feed — vertical stack of reply rows, newest first. */}
      {allReplies.length > 0 ? (
        <div className="space-y-2">
          {allReplies.map((r) => (
            <ReplyRow key={r.id} reply={r} />
          ))}

          {/* Load-more — manual rather than scroll-triggered because
              the page tail is also a natural "I've caught up" signal;
              a click confirms intent before pulling more rows. */}
          {repliesQuery.hasNextPage ? (
            <div className="pt-2 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => repliesQuery.fetchNextPage()}
                disabled={repliesQuery.isFetchingNextPage}
              >
                {repliesQuery.isFetchingNextPage ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>Load more replies</>
                )}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
